<template>
  <div class="darwin-dialog-layout">
    <div class="darwin-dialog-layout__header">
      <p class="darwin-dialog-layout__header-title">
        <slot name="title" />
      </p>
      <IconButton
        v-if="showCloseButton"
        class="darwin-dialog-layout__header-close"
        aria-label="Close dialog"
        kind="transparent"
        size="sm"
        flair="rounded"
        @click="emit('close')"
      >
        <Icon
          name="mono-close"
          class="darwin-dialog-layout__header-close-icon"
        />
      </IconButton>
    </div>

    <div class="darwin-dialog-layout__body">
      <slot name="body" />
    </div>

    <div
      v-if="showFooter"
      class="darwin-dialog-layout__footer"
    >
      <div
        v-if="$slots['footer-prepend']"
        class="darwin-dialog-layout__footer-prepend"
      >
        <slot name="footer-prepend" />
      </div>
      <div class="darwin-dialog-layout__footer-actions">
        <slot name="footer-buttons">
          <DarwinButton
            v-if="showCancelButton"
            class="darwin-dialog-layout__footer-actions-cancel"
            :class="{ 'darwin-dialog-layout__footer-actions-cancel--full': !showConfirmButton }"
            name="cancel"
            aria-label="Cancel"
            size="md"
            flair="rounded"
            kind="outline"
            @click="$emit('cancel')"
          >
            <slot name="cancel-text"> Cancel </slot>
          </DarwinButton>
          <DarwinButton
            v-if="showConfirmButton"
            class="darwin-dialog-layout__footer-actions-confirm"
            :class="{ 'darwin-dialog-layout__footer-actions-confirm--full': !showCancelButton }"
            name="confirm"
            :kind="kind"
            size="md"
            flair="rounded"
            :disabled="isConfirmDisabled"
            @click="$emit('confirm')"
          >
            <slot name="confirm-text"> Confirm </slot>
          </DarwinButton>
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import type { DarwinKindType } from '@/uiKit/types'

withDefaults(
  defineProps<{
    showCloseButton?: boolean
    showCancelButton?: boolean
    showConfirmButton?: boolean
    showFooter?: boolean
    isConfirmDisabled?: boolean
    tooltipCancelText?: string
    tooltipConfirmText?: string
    kind?: DarwinKindType
  }>(),
  {
    showCloseButton: true,
    showFooter: true,
    showCancelButton: true,
    showConfirmButton: true,
    tooltipCancelText: undefined,
    tooltipConfirmText: undefined,
    kind: 'primary',
  },
)

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()
</script>

<style lang="scss" scoped>
@import '@/assets/styles/shared/scrollbarV2';

.darwin-dialog-layout {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1 1 auto;
  height: inherit;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: var(--size-16);
    gap: var(--size-4);
    padding: var(--size-4);

    &-title {
      @include typographyBoldBody500;
      color: var(--color-text-default);
    }
  }

  &__body {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    flex: 1 1 auto;
    height: max-content;
    max-height: 100%;
    width: 100%;
    gap: var(--size-4);
    padding: 0 var(--size-4);
    @include scrollbarV2('vertical');
    @include typographyRegularBody200;
    color: var(--color-text-subtlest);
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: var(--size-16);
    gap: var(--size-4);
    padding: var(--size-4);
    background-color: var(--color-background-gray-subtlest);
    border-top: 1px solid var(--color-border-default);
    border-bottom-right-radius: var(--size-4);
    border-bottom-left-radius: var(--size-4);

    &-prepend {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex: 1;
      height: var(--size-16);
      gap: var(--size-4);
    }

    &-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex: 1;
      gap: var(--size-4);

      &-cancel,
      &-confirm {
        display: flex;
        justify-content: center;
        align-items: center;
        width: fit-content;

        &--full {
          width: 100%;
        }
      }
    }
  }
}
</style>
